import { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { suggestVehicles } from "@/lib/vehicle";
import { Sparkle, Camera, X as XIcon, Crop } from "@phosphor-icons/react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

function getCroppedBlob(imgEl, crop) {
  return new Promise((resolve) => {
    if (!crop || !crop.width || !crop.height) { resolve(null); return; }
    const canvas = document.createElement("canvas");
    const scaleX = imgEl.naturalWidth / imgEl.width;
    const scaleY = imgEl.naturalHeight / imgEl.height;
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(imgEl, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => resolve(blob), "image/png", 1.0);
  });
}

const EMPTY = { info_block: "", pickup_date: "", pickup_time: "", route_label: "", ride_type: "eco", service_type: "transfert", driver_fare: "", client_price: "", driver_amount: "", is_pab: false, passenger_count: "", luggage_count: "", notes: "", is_round_trip: false, return_date: "", return_time: "",
  // Phase 31 — Passenger vs Billing Client separation (optional, B2C default).
  passenger_name: "", passenger_phone: "",
  billing_client_name: "", billing_client_type: "B2C", billing_client_email: "", billing_client_contact: "",
  payment_type: "standard",
  // Phase 34 — document preparation intent flags (default unchecked).
  invoice_requested: false, voucher_requested: false,
  // Phase 35 — language for future PDF documents (default "fr").
  document_language: "fr",
  // UI-only flag: shows the B2B / billing fields block when true.
  _b2b_expanded: false };

export default function CreateRideModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ ...EMPTY });
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiStatus, setAiStatus] = useState("");
  // Crop state
  const [cropSrc, setCropSrc] = useState(null);
  const [crop, setCrop] = useState(undefined);
  const [showCrop, setShowCrop] = useState(false);
  const [rawFile, setRawFile] = useState(null);
  const imgCropRef = useRef(null);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: typeof e === "string" ? e : e.target.value }));

  const applyAIResult = (data) => {
    setForm(p => ({
      ...p,
      info_block: data.info_block || p.info_block,
      route_label: data.route_label || p.route_label,
      pickup_date: data.pickup_date || p.pickup_date,
      pickup_time: data.pickup_time || p.pickup_time,
      ride_type: data.ride_type || p.ride_type,
      passenger_count: data.passenger_count != null ? String(data.passenger_count) : p.passenger_count,
      luggage_count: data.luggage_count != null ? String(data.luggage_count) : p.luggage_count,
      notes: data.notes || p.notes,
    }));
  };

  const aiAutofillText = async () => {
    if (!form.info_block.trim()) { toast.error("Paste booking text first"); return; }
    setAiLoading(true);
    setAiStatus("Analyzing text...");
    try {
      const { data } = await api.post("/rides/ai-parse", { text: form.info_block });
      applyAIResult(data);
      toast.success("Fields auto-filled. Please review.");
    } catch { toast.error("AI parsing failed. Fill manually."); }
    finally { setAiLoading(false); setAiStatus(""); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image"); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("Image too large (max 20MB)"); return; }
    setRawFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCropSrc(ev.target.result);
      setCrop(undefined);
      setShowCrop(true);
    };
    reader.readAsDataURL(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const sendImageToOCR = useCallback(async (blob) => {
    setAiLoading(true);
    setAiStatus("Lecture du screenshot...");
    try {
      const formData = new FormData();
      formData.append("file", blob, "screenshot.png");
      const { data } = await api.post("/rides/ai-parse-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 40000,
      });
      // Debug visibility — inspect what was extracted without blocking user.
      console.log("[AI image] source:", data?._source, "status:", data?._extraction_status);
      console.log("[AI image] OCR text:", data?._ocr_text || "(empty)");
      console.log("[AI image] parsed fields:", data);
      applyAIResult(data);
      const status = data?._extraction_status;
      if (status === "full") toast.success("Champs pré-remplis — vérifiez avant d'enregistrer");
      else if (status === "partial") toast.info("Lecture partielle — veuillez compléter les informations manquantes");
      else toast.info("Aucune donnée détectée — veuillez remplir manuellement");
    } catch (err) {
      // Treat any error as partial extraction — never block the user.
      console.warn("[AI image] request failed:", err?.response?.data || err?.message);
      toast.info("Lecture partielle — veuillez compléter les informations manquantes");
    } finally {
      setAiLoading(false);
      setAiStatus("");
      setCropSrc(null);
      setShowCrop(false);
    }
  }, []);

  const handleCropConfirm = async () => {
    if (crop && crop.width > 10 && crop.height > 10 && imgCropRef.current) {
      const blob = await getCroppedBlob(imgCropRef.current, crop);
      if (blob) { sendImageToOCR(blob); return; }
    }
    // No crop or too small: send full image
    if (rawFile) { sendImageToOCR(rawFile); }
  };

  const handleSkipCrop = () => {
    if (rawFile) sendImageToOCR(rawFile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.pickup_date || !form.pickup_time || !form.route_label || !form.driver_fare) { toast.error("Date, time, route and fare required"); return; }
    setLoading(true);
    try {
      await api.post("/rides", {
        info_block: form.info_block, pickup_date: form.pickup_date, pickup_time: form.pickup_time,
        route_label: form.route_label, ride_type: form.ride_type,
        service_type: form.service_type,
        driver_fare: parseFloat(form.driver_fare),
        // Phase 30 — financial structure (optional; backend mirrors driver_fare when blank)
        client_price: form.client_price !== "" ? parseFloat(form.client_price) : null,
        driver_amount: form.driver_amount !== "" ? parseFloat(form.driver_amount) : null,
        is_pab: !!form.is_pab,
        passenger_count: form.passenger_count ? parseInt(form.passenger_count) : null,
        luggage_count: form.luggage_count ? parseInt(form.luggage_count) : null,
        notes: form.notes,
        is_round_trip: form.is_round_trip,
        return_date: form.return_date || null,
        return_time: form.return_time || null,
        // ─── Phase 31 · Passenger vs Billing Client separation ───
        // Send only what was filled; backend falls back to client_name on
        // legacy. When the B2B block is collapsed, we still send the
        // payment_type (standard/PAB) which is always visible.
        passenger_name: form.passenger_name.trim() || null,
        passenger_phone: form.passenger_phone.trim() || null,
        billing_client_name: form._b2b_expanded ? (form.billing_client_name.trim() || null) : null,
        billing_client_type: form._b2b_expanded ? form.billing_client_type : "B2C",
        billing_client_email: form._b2b_expanded ? (form.billing_client_email.trim() || null) : null,
        billing_client_contact: form._b2b_expanded ? (form.billing_client_contact.trim() || null) : null,
        payment_type: form.payment_type,
        // ─── Phase 34 · dispatch document intent (pure flags, no PDF here) ───
        invoice_requested: !!form.invoice_requested,
        voucher_requested: !!form.voucher_requested,
        // ─── Phase 35 · document language (whitelisted server-side) ───
        document_language: form.document_language || "fr",
      });
      toast.success(form.is_round_trip ? "Round trip created (2 rides)" : "Ride created");
      setForm({ ...EMPTY });
      setCropSrc(null); setShowCrop(false);
      onCreated?.(); onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to create ride");
    } finally { setLoading(false); }
  };

  const resetAll = () => {
    setForm({ ...EMPTY });
    setCropSrc(null); setShowCrop(false); setRawFile(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetAll(); onClose(); }}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-medium">New Ride</DialogTitle>
          <DialogDescription className="text-zinc-500">Upload screenshot, paste text, or fill manually</DialogDescription>
        </DialogHeader>

        {/* Crop View */}
        {showCrop && cropSrc && (
          <div className="space-y-3">
            <p className="text-sm text-zinc-400">Crop to the booking text area, or send the full image:</p>
            <div className="max-h-[50vh] overflow-auto rounded border border-zinc-700 bg-black">
              <ReactCrop crop={crop} onChange={c => setCrop(c)} className="max-w-full">
                <img ref={imgCropRef} src={cropSrc} alt="Screenshot" className="max-w-full" style={{ maxHeight: "50vh" }} />
              </ReactCrop>
            </div>
            <div className="flex gap-2">
              <Button type="button" onClick={handleCropConfirm} disabled={aiLoading} className="flex-1 bg-amber-500 text-black font-semibold hover:bg-amber-600 h-10">
                <Crop size={16} className="mr-1.5" /> {crop?.width > 10 ? "Read Cropped Area" : "Read Full Image"}
              </Button>
              <Button type="button" onClick={handleSkipCrop} disabled={aiLoading} variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-10">
                Full Image
              </Button>
              <Button type="button" onClick={() => { setCropSrc(null); setShowCrop(false); }} variant="ghost" className="text-zinc-500 hover:text-white h-10 px-3">
                <XIcon size={16} />
              </Button>
            </div>
            {aiLoading && <div className="flex items-center gap-2 text-sm text-amber-500"><div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />{aiStatus}</div>}
          </div>
        )}

        {/* Main Form (shown when not cropping) */}
        {!showCrop && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* AI Actions Bar */}
            <div className="flex gap-2">
              <Button type="button" data-testid="screenshot-upload-btn" onClick={() => fileRef.current?.click()} disabled={aiLoading} variant="outline" className="flex-1 h-10 border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white">
                <Camera size={16} className="mr-1.5" /> {aiLoading ? aiStatus || "Reading..." : "Screenshot"}
              </Button>
              <Button type="button" data-testid="ai-autofill-btn" onClick={aiAutofillText} disabled={aiLoading || !form.info_block.trim()} variant="outline" className="flex-1 h-10 border-amber-500/30 text-amber-500 hover:bg-amber-500/10">
                <Sparkle size={16} weight="fill" className="mr-1.5" /> {aiLoading ? "Parsing..." : "Auto-fill AI"}
              </Button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} data-testid="screenshot-file-input" />

            {aiLoading && !showCrop && <div className="flex items-center gap-2 text-sm text-amber-500"><div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />{aiStatus}</div>}

            {/* Text block */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Ride Information (copy/paste)</Label>
              <Textarea data-testid="ride-info-block" value={form.info_block} onChange={set("info_block")} placeholder="Paste booking info or upload screenshot..." className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 min-h-[100px] text-sm" />
            </div>

            {/* Structured fields */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-zinc-400 text-xs uppercase tracking-wider">Date *</Label><Input data-testid="ride-pickup-date" type="date" value={form.pickup_date} onChange={set("pickup_date")} required className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 h-10" /></div>
              <div className="space-y-1.5"><Label className="text-zinc-400 text-xs uppercase tracking-wider">Time *</Label><Input data-testid="ride-pickup-time" type="time" value={form.pickup_time} onChange={set("pickup_time")} required className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 h-10" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-zinc-400 text-xs uppercase tracking-wider">Route *</Label>
              <div className="flex gap-2">
                <Input data-testid="ride-route-label" value={form.route_label} onChange={set("route_label")} placeholder="CDG → Paris 8" required className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10 text-base font-medium flex-1" />
                <Button type="button" data-testid="auto-price-btn" variant="outline" onClick={async () => {
                  const parts = (form.route_label || "").split("→");
                  if (parts.length !== 2) { toast.error("Format attendu : Départ → Arrivée"); return; }
                  try {
                    const { data } = await api.get("/admin/pricing-routes/match", { params: { from_location: parts[0].trim(), to_location: parts[1].trim(), vehicle_type: form.ride_type } });
                    if (data.match) { setForm(p => ({ ...p, driver_fare: String(data.match.default_price) })); toast.success(`Prix auto-rempli : ${data.match.default_price} EUR`); }
                    else toast.error("Aucun tarif prédéfini trouvé");
                  } catch { toast.error("Échec"); }
                }} className="h-10 shrink-0 border-amber-500/30 text-amber-500 hover:bg-amber-500/10">Tarif auto</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Type *</Label>
                <Select value={form.ride_type} onValueChange={(v) => setForm(p => ({...p, ride_type: v, ...(v === "moto" ? { passenger_count: "1", luggage_count: "0" } : {}) }))}>
                  <SelectTrigger data-testid="ride-type-select" className="bg-[#1A1A1A] border-zinc-700 text-white h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-zinc-700">
                    <SelectItem value="eco" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">Eco</SelectItem>
                    <SelectItem value="berline" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">Berline</SelectItem>
                    <SelectItem value="tesla" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">Tesla</SelectItem>
                    <SelectItem value="van" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">Van</SelectItem>
                    <SelectItem value="moto" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">Moto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-zinc-400 text-xs uppercase tracking-wider">Fare (EUR) *</Label><Input data-testid="ride-driver-fare" type="number" step="0.01" value={form.driver_fare} onChange={set("driver_fare")} placeholder="75" required className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" /></div>
            </div>
            {/* Phase 30 — financial structure. Optional fields. Leave blank to mirror Fare. */}
            <div className="border border-zinc-800 rounded-lg p-3 space-y-3 bg-[#0e0e0e]">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Détail financier (optionnel)</Label>
                {(() => {
                  const cp = parseFloat(form.client_price || form.driver_fare || 0) || 0;
                  const da = parseFloat(form.driver_amount || form.driver_fare || 0) || 0;
                  const com = Math.max(0, cp - da);
                  return <span data-testid="finance-commission-preview" className="text-[11px] text-amber-400">Commission auto · <strong>{com.toFixed(2)} EUR</strong></span>;
                })()}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-zinc-500 text-[11px] uppercase tracking-wider">Prix client (EUR)</Label><Input data-testid="ride-client-price" type="number" step="0.01" value={form.client_price} onChange={set("client_price")} placeholder={form.driver_fare || "auto"} className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" /></div>
                <div className="space-y-1.5"><Label className="text-zinc-500 text-[11px] uppercase tracking-wider">Montant chauffeur (EUR)</Label><Input data-testid="ride-driver-amount" type="number" step="0.01" value={form.driver_amount} onChange={set("driver_amount")} placeholder={form.driver_fare || "auto"} className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer select-none">
                <input data-testid="ride-is-pab" type="checkbox" checked={!!form.is_pab} onChange={(e) => setForm(p => ({ ...p, is_pab: e.target.checked }))} className="h-4 w-4 accent-amber-500" />
                <span>PAB <span className="text-zinc-500 text-xs">— le chauffeur voit le prix client</span></span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Service</Label>
                <Select value={form.service_type} onValueChange={(v) => setForm(p => ({...p, service_type: v}))}>
                  <SelectTrigger data-testid="ride-service-select" className="bg-[#1A1A1A] border-zinc-700 text-white h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-zinc-700">
                    <SelectItem value="transfert" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">Transfert</SelectItem>
                    <SelectItem value="mise_a_disposition" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">Mise à disposition</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.ride_type === "moto" && <p className="text-[11px] text-amber-400 self-end pb-1">Moto : 1 passager max · 0 bagage</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-zinc-400 text-xs uppercase tracking-wider">Passengers</Label><Input data-testid="ride-passenger-count" type="number" min="0" value={form.passenger_count} onChange={set("passenger_count")} placeholder="0" className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" /></div>
              <div className="space-y-1.5"><Label className="text-zinc-400 text-xs uppercase tracking-wider">Luggage</Label><Input data-testid="ride-luggage-count" type="number" min="0" value={form.luggage_count} onChange={set("luggage_count")} placeholder="0" className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" /></div>
            </div>
            {(() => {
              const p = parseInt(form.passenger_count || "0");
              if (!p || p < 5) return null;
              const s = suggestVehicles(form.passenger_count, form.luggage_count);
              return (
                <p data-testid="vehicle-suggestion" className="text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded p-2">
                  Suggestion : {s.label}{s.heavy ? " — bagages importants pris en compte." : ""} (modifiable librement)
                </p>
              );
            })()}
            <div className="space-y-1.5"><Label className="text-zinc-400 text-xs uppercase tracking-wider">Notes</Label><Textarea data-testid="ride-notes" value={form.notes} onChange={set("notes")} placeholder="Additional notes..." className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 min-h-[60px] text-sm" /></div>

            {/* ─── Phase 31 · Passenger vs Billing Client + Payment mode ─── */}
            <div className="space-y-3 p-3 bg-[#1A1A1A] border border-zinc-800 rounded">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs text-zinc-400 uppercase tracking-wider">Facturation</p>
                  <p className="text-[11px] text-zinc-500">Par défaut B2C — activer pour un client différent du passager (B2B).</p>
                </div>
                <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer select-none">
                  <input
                    data-testid="ride-b2b-toggle"
                    type="checkbox"
                    checked={!!form._b2b_expanded}
                    onChange={(e) => setForm(p => ({ ...p, _b2b_expanded: e.target.checked, billing_client_type: e.target.checked ? "B2B" : "B2C" }))}
                    className="h-4 w-4 accent-amber-500"
                  />
                  <span>Client facturé différent (B2B)</span>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Passager (transporté)</Label>
                  <Input data-testid="ride-passenger-name" value={form.passenger_name} onChange={set("passenger_name")} placeholder="Nom du passager" className="bg-[#0a0a0a] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Téléphone passager</Label>
                  <Input data-testid="ride-passenger-phone" value={form.passenger_phone} onChange={set("passenger_phone")} placeholder="+33…" className="bg-[#0a0a0a] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
                </div>
              </div>
              {form._b2b_expanded && (
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-zinc-800">
                  <div className="space-y-1.5 col-span-2">
                    <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Client facturé (B2B)</Label>
                    <Input data-testid="ride-billing-client-name" value={form.billing_client_name} onChange={set("billing_client_name")} placeholder="Ex. Agence Travel Paris" className="bg-[#0a0a0a] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Email facturation</Label>
                    <Input data-testid="ride-billing-client-email" type="email" value={form.billing_client_email} onChange={set("billing_client_email")} placeholder="billing@…" className="bg-[#0a0a0a] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Contact facturation</Label>
                    <Input data-testid="ride-billing-client-contact" value={form.billing_client_contact} onChange={set("billing_client_contact")} placeholder="Téléphone / référent" className="bg-[#0a0a0a] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-zinc-800">
                <div className="space-y-1.5">
                  <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Mode de paiement</Label>
                  <Select value={form.payment_type} onValueChange={(v) => setForm(p => ({...p, payment_type: v}))}>
                    <SelectTrigger data-testid="ride-payment-type" className="bg-[#0a0a0a] border-zinc-700 text-white h-10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-zinc-700">
                      <SelectItem value="standard" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">Standard (payé à la société)</SelectItem>
                      <SelectItem value="pab" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">PAB — Paiement à bord (chauffeur)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-[11px] text-zinc-500 self-end pb-2">{form.payment_type === "pab" ? "Le chauffeur encaisse directement le client." : "Client paie la société, vous réglez le chauffeur."}</p>
              </div>
            </div>

            {/* Round trip */}
            <div className="flex items-center gap-3 p-3 bg-[#1A1A1A] border border-zinc-700 rounded">
              <label className="flex items-center gap-2 cursor-pointer flex-1">
                <input data-testid="round-trip-toggle" type="checkbox" checked={form.is_round_trip} onChange={(e) => setForm(p => ({...p, is_round_trip: e.target.checked}))} className="w-4 h-4 rounded border-zinc-600 text-amber-500 focus:ring-amber-500 bg-[#0a0a0a]" />
                <span className="text-sm text-white font-medium">Round Trip (Aller / Retour)</span>
              </label>
            </div>
            {form.is_round_trip && (
              <div className="grid grid-cols-2 gap-3 p-3 bg-[#1A1A1A] border border-zinc-700 rounded">
                <div className="space-y-1.5"><Label className="text-zinc-400 text-xs uppercase tracking-wider">Return Date</Label><Input data-testid="ride-return-date" type="date" value={form.return_date} onChange={set("return_date")} className="bg-[#0a0a0a] border-zinc-600 text-white focus:border-amber-500 h-10" /></div>
                <div className="space-y-1.5"><Label className="text-zinc-400 text-xs uppercase tracking-wider">Return Time</Label><Input data-testid="ride-return-time" type="time" value={form.return_time} onChange={set("return_time")} className="bg-[#0a0a0a] border-zinc-600 text-white focus:border-amber-500 h-10" /></div>
              </div>
            )}

            {/* Phase 34 — Documents à préparer (intent flags, no PDF generation here). */}
            <div className="border border-zinc-800 rounded-lg p-3 space-y-2 bg-[#0e0e0e]">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Documents à préparer</Label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  data-testid="ride-invoice-requested"
                  type="checkbox"
                  checked={!!form.invoice_requested}
                  onChange={(e) => setForm(p => ({ ...p, invoice_requested: e.target.checked }))}
                  className="w-4 h-4 rounded border-zinc-600 text-amber-500 focus:ring-amber-500 bg-[#0a0a0a]"
                />
                <span className="text-sm text-white">Facture client</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  data-testid="ride-voucher-requested"
                  type="checkbox"
                  checked={!!form.voucher_requested}
                  onChange={(e) => setForm(p => ({ ...p, voucher_requested: e.target.checked }))}
                  className="w-4 h-4 rounded border-zinc-600 text-amber-500 focus:ring-amber-500 bg-[#0a0a0a]"
                />
                <span className="text-sm text-white">Voucher / confirmation de transfert</span>
              </label>
              <p className="text-[11px] text-zinc-500">Les documents seront générables à la demande après la création de la course.</p>
              <div className="space-y-1.5 pt-2 border-t border-zinc-800">
                <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Langue des documents</Label>
                <Select value={form.document_language} onValueChange={(v) => setForm(p => ({...p, document_language: v}))}>
                  <SelectTrigger data-testid="ride-document-language" className="bg-[#0a0a0a] border-zinc-700 text-white h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-zinc-700">
                    <SelectItem value="fr" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">Français uniquement</SelectItem>
                    <SelectItem value="fr_en" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">Français + Anglais</SelectItem>
                    <SelectItem value="fr_es" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">Français + Espagnol</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-zinc-600 leading-tight">Le français est toujours présent. La 2ᵉ langue ne sera utilisée que par la facture et le voucher.</p>
              </div>
            </div>

            <Button data-testid="create-ride-submit" type="submit" disabled={loading} className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 active:scale-[0.98] h-11">
              {loading ? "Creating..." : "Create Ride"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
